import { useMemo, useState, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { Inbox, Search, type LucideIcon } from 'lucide-react';

/**
 * The admin's shared furniture.
 *
 * Sixteen sections behave the same way — search, filter, a table, a status
 * chip, an empty state — and sixteen hand-built versions of that would drift
 * apart within a month. Everything here is deliberately unopinionated about
 * WHAT is listed; the pages supply the columns and the rows.
 */

/* --------------------------------------------------------------- toolbar */

/**
 * One dropdown in a toolbar.
 *
 * `value` and `onChange` are REQUIRED, and that is the whole point. They used
 * to be optional, and a filter supplied without them rendered as a `disabled`
 * select with nothing beside it saying why — six sections shipped a row of
 * dead grey dropdowns over tables they could not narrow. A filter that cannot
 * filter is now a type error rather than a screen a dispatcher has to learn to
 * ignore.
 */
export interface ToolbarFilter {
  readonly label: string;
  readonly options: readonly string[];
  /** Current selection. The first option always means "no filter". */
  readonly value: string;
  readonly onChange: (value: string) => void;
}

export function Toolbar({
  placeholder,
  filters = [],
  right,
  query,
  onQuery,
}: {
  readonly placeholder: string;
  readonly filters?: readonly ToolbarFilter[];
  readonly right?: ReactNode;
  /**
   * Controlled search text.
   *
   * Optional ONLY so a toolbar with nothing to search can still show its
   * actions. When it is omitted the input is not rendered at all — an inert
   * search box over a table of bookings is worse than no search box, because
   * a dispatcher types a booking reference into it and concludes the
   * reference does not exist.
   */
  readonly query?: string;
  readonly onQuery?: (value: string) => void;
}): ReactElement {
  const searchable = query !== undefined && onQuery !== undefined;
  return (
    <div className="cl-toolbar">
      {searchable ? (
        <label className="cl-search">
          <Search size={15} strokeWidth={2} aria-hidden="true" />
          <input
            type="search"
            placeholder={placeholder}
            aria-label={placeholder}
            value={query}
            onChange={(event) => onQuery(event.target.value)}
          />
        </label>
      ) : null}
      {filters.map((filter) => (
        <select
          key={filter.label}
          className="cl-select"
          aria-label={filter.label}
          value={filter.value}
          onChange={(event) => filter.onChange(event.target.value)}
        >
          {filter.options.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      ))}
      {right === undefined ? null : <div className="cl-toolbar__right">{right}</div>}
    </div>
  );
}

/**
 * Search and filter a table, for the sixteen sections that all need it.
 *
 * Returns the surviving rows plus the props the `Toolbar` needs, so a page
 * wires this in three lines and cannot accidentally render a control that
 * filters nothing.
 *
 * Matching is case-insensitive and substring-based against whatever text the
 * page decides identifies a row. Deliberately not fuzzy: a dispatcher reading
 * a booking reference off the phone wants that reference or nothing, and a
 * near-match presented as a hit is how the wrong load gets cancelled.
 */
export function useToolbar<Row>(
  rows: readonly Row[],
  searchable: (row: Row) => string,
  filters: readonly {
    readonly label: string;
    readonly options: readonly string[];
    readonly match: (row: Row, option: string) => boolean;
  }[] = [],
  /**
   * The chosen filters, held by the PAGE instead of by this hook.
   *
   * WHY IT CAN BE LIFTED OUT. Every filter here ran in the browser over the
   * rows the page had already fetched, and those pages fetch fifty. So the
   * Bookings "Status: Searching" dropdown answered "which of the fifty most
   * recent loads are searching", while looking exactly like "which loads are
   * searching" — a stuck booking from yesterday was simply not in the list.
   *
   * A page that can send the filter to the server needs to know the choice
   * BEFORE it queries, and the query produces the rows this hook is given. So
   * the page may own the state and pass it here. Pages that filter purely in
   * the browser leave this out and nothing changes for them.
   */
  control?: {
    readonly chosen: Readonly<Record<string, string>>;
    readonly setChosen: (next: Readonly<Record<string, string>>) => void;
  },
): {
  readonly rows: readonly Row[];
  readonly toolbar: {
    readonly query: string;
    readonly onQuery: (value: string) => void;
    readonly filters: readonly ToolbarFilter[];
  };
  /** True when a control is narrowing the list — for an honest empty state. */
  readonly narrowed: boolean;
} {
  const [query, setQuery] = useState('');
  const [ownChosen, setOwnChosen] = useState<Readonly<Record<string, string>>>({});
  const chosen = control?.chosen ?? ownChosen;
  const setChosen = control?.setChosen ?? setOwnChosen;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (needle !== '' && !searchable(row).toLowerCase().includes(needle)) return false;
      for (const filter of filters) {
        const value = chosen[filter.label] ?? filter.options[0];
        // The first option is always the "all" case and filters nothing.
        if (value !== undefined && value !== filter.options[0] && !filter.match(row, value)) {
          return false;
        }
      }
      return true;
    });
    /*
      Deliberately NOT depending on `searchable` or `filters`.

      Both are redefined on every render by the calling page — a fresh arrow
      function and a fresh array literal each time — so depending on them would
      recompute on every render and defeat the memo entirely. The rows, the
      query and the chosen filters are what actually change the answer.
    */
  }, [rows, query, chosen]);

  return {
    rows: visible,
    narrowed:
      query.trim() !== '' ||
      filters.some((f) => (chosen[f.label] ?? f.options[0]) !== f.options[0]),
    toolbar: {
      query,
      onQuery: setQuery,
      filters: filters.map((filter) => ({
        label: filter.label,
        options: filter.options,
        value: chosen[filter.label] ?? filter.options[0] ?? '',
        onChange: (value: string) => setChosen({ ...chosen, [filter.label]: value }),
      })),
    },
  };
}

/**
 * A figure, or an honest dash.
 *
 * `ready` is "the query that produces this has answered". Until it has, a
 * summary tile must not print a number: `rows.length` over an empty array
 * while a request is in flight renders a confident `0`, and `₹0` on the
 * payments page is a dispatcher concluding the platform took no money today.
 * An em dash says "not known yet" and is the same shape the tables already use
 * for an absent cell.
 *
 * Deliberately a function of a boolean rather than of the query object: a
 * failed request and an unfinished one are both "no answer", and a tile that
 * showed 0 on an error would be the same lie with a longer fuse.
 */
export function figure(ready: boolean, value: () => string): string {
  return ready ? value() : '—';
}

/* ----------------------------------------------------------------- table */

export interface Column<Row> {
  readonly key: string;
  /**
   * The column header. Always required, even for a column of buttons —
   * an empty `<th>` announces nothing, so a screen-reader user meets a
   * nameless column exactly where the actions are. Use `hideHead` to keep it
   * out of sight without taking it away.
   */
  readonly head: string;
  /** Renders the header for sighted users as nothing, but keeps it announced. */
  readonly hideHead?: boolean;
  /** Right-aligns and switches to tabular figures. Use for money and counts. */
  readonly numeric?: boolean;
  readonly cell: (row: Row) => ReactNode;
}

/**
 * A table that scrolls inside its own box.
 *
 * The dashboard shipped a table that pushed the whole PAGE sideways by 46px
 * because it had a floor width its card could not give it. Every table here
 * gets the scroll box and the keyboard affordance by construction, so that
 * defect cannot be reintroduced one page at a time.
 */
export function Table<Row>({
  label,
  columns,
  rows,
  keyOf,
  empty = 'Nothing to show yet.',
  minWidth = 640,
  onRow,
}: {
  readonly label: string;
  readonly columns: readonly Column<Row>[];
  readonly rows: readonly Row[];
  readonly keyOf: (row: Row) => string;
  readonly empty?: string;
  readonly minWidth?: number;
  /**
   * Opens a row when it is clicked anywhere. A pointer convenience only: the
   * row must still carry a link or button, which is what a keyboard reaches.
   */
  readonly onRow?: (row: Row) => void;
}): ReactElement {
  if (rows.length === 0) return <Empty>{empty}</Empty>;
  return (
    <div className="cl-scroll" tabIndex={0} role="region" aria-label={`${label}, scrollable`}>
      <table className="cl-table" style={{ minWidth }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.numeric === true ? 'cl-num' : undefined}>
                {column.hideHead === true ? (
                  <span className="cl-vh">{column.head}</span>
                ) : (
                  column.head
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={keyOf(row)}
              {...(onRow === undefined
                ? {}
                : {
                    className: 'cl-row--open',
                    onClick: (event: MouseEvent<HTMLTableRowElement>) => {
                      // A click on the row's own link or button is that control's.
                      if ((event.target as HTMLElement).closest('a,button') !== null) return;
                      onRow(row);
                    },
                  })}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={column.numeric === true ? 'cl-num cl-mono' : undefined}
                >
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------- pieces */

export type Tone = 'success' | 'danger' | 'warning' | 'info' | 'pending' | 'neutral';

export function Badge({
  label,
  tone,
}: {
  readonly label: string;
  readonly tone: Tone;
}): ReactElement {
  return <span className={`cl-badge cl-badge--${tone}`}>{label}</span>;
}

/** Name over a quiet second line — a phone number, a registration, an email. */
export function Person({
  name,
  meta,
  chip,
}: {
  readonly name: string;
  readonly meta?: string;
  readonly chip?: string;
}): ReactElement {
  return (
    <span className="cl-person">
      <span className="cl-face" aria-hidden="true">
        {name.charAt(0)}
      </span>
      <span className="cl-person__body">
        <strong>{name}</strong>
        {meta === undefined ? null : (
          <span className="cl-person__meta">
            {meta}
            {chip === undefined ? null : <em className="cl-chip">{chip}</em>}
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * "There is nothing here", said in the console's own visual language.
 *
 * The icon is the same tinted chip the stat tiles and the dashboard's feeds
 * use, so an empty table reads as a deliberate state of this product rather
 * than as a card that failed to paint. Neutral, never amber: an empty list is
 * usually good news, and colouring it as a problem teaches an operator to
 * distrust the colours that are.
 */
export function Empty({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <div className="cl-empty">
      <span className="cl-tile cl-tile--neutral" aria-hidden="true">
        <Inbox size={18} strokeWidth={2} />
      </span>
      <p className="cl-empty__text">{children}</p>
    </div>
  );
}

/** A card with a heading. The dashboard's `Card`, minus its chart tooling. */
export function Panel({
  title,
  tools,
  children,
}: {
  readonly title?: string;
  readonly tools?: ReactNode;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section className="cl-card">
      {title === undefined ? null : (
        <header className="cl-card__head">
          <h2 className="cl-card__title">{title}</h2>
          {tools === undefined ? null : <div className="cl-card__tools">{tools}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * A metric's tint, chosen from the tone the caller already states.
 *
 * The tile palette and the status palette are the same six hues under two
 * names, so this is a rename rather than a second colour decision — which is
 * the point: a page says `tone: 'danger'` once and the badge, the figure and
 * the icon chip agree without anybody re-deciding what red means.
 */
const TONE_TILE: Record<Tone, string> = {
  success: 'green',
  danger: 'red',
  warning: 'orange',
  info: 'blue',
  pending: 'purple',
  neutral: 'neutral',
};

/**
 * The strip of figures that opens every section — the dashboard's stat tile,
 * reused rather than imitated.
 *
 * This used to be a deliberately quieter thing: a grey label over a coloured
 * number, no icon. Beside the dashboard it read as unfinished rather than as
 * restrained, so it now renders the SAME `cl-stat` markup the dashboard does.
 * Sharing the classes, not copying them, is what stops the two drifting apart
 * the next time one of them is adjusted.
 *
 * `icon` is required on purpose. An optional one would be omitted on the page
 * somebody is in a hurry on, and a single iconless tile in a row of six is
 * more conspicuous than none of them having icons at all.
 */
export function Summary({
  items,
  label = 'At a glance',
}: {
  readonly items: readonly {
    readonly label: string;
    readonly value: string;
    readonly icon: LucideIcon;
    readonly tone?: Tone;
  }[];
  /** Names the region, the way the dashboard names its own strip of stats. */
  readonly label?: string;
}): ReactElement {
  return (
    <section className="cl-summary" aria-label={label}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          /*
            The figure itself stays ink-black, as it is on the dashboard. The
            tone now rides on the icon chip instead, because colouring BOTH
            says the same thing twice and, on a row where three of four tiles
            carry a tone, leaves the reader with four differently-coloured
            numbers and no idea which one is the exception.
          */
          <article key={item.label} className="cl-stat cl-summary__cell">
            <span className="cl-stat__top">
              <span
                className={`cl-tile cl-tile--${TONE_TILE[item.tone ?? 'neutral']}`}
                aria-hidden="true"
              >
                <Icon size={19} strokeWidth={2} />
              </span>
              <span className="cl-stat__label">{item.label}</span>
            </span>
            <strong className="cl-stat__value">{item.value}</strong>
          </article>
        );
      })}
    </section>
  );
}

/**
 * A labelled fact, not a form control.
 *
 * READ-ONLY TEXT. It used to render `<input defaultValue={...}>` on Settings —
 * a box that invited an operator to type a new support number, accepted it,
 * and discarded it on the next render because nothing saved. A control that
 * takes input and throws it away is worse than a label.
 *
 * The value is a node rather than a string because the second page to need
 * this puts a `Badge` in one and a sentence in the next: a driver with no
 * rating gets "No rating yet", and that is a fact about them, not a blank.
 */
export function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: ReactNode;
}): ReactElement {
  return (
    <div className="cl-field">
      <span className="cl-field__label">{label}</span>
      <span className="cl-field__value">{value}</span>
    </div>
  );
}

/** Buttons at the end of a table row. */
export function RowActions({ children }: { readonly children: ReactNode }): ReactElement {
  return <span className="cl-rowacts">{children}</span>;
}
